import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, Heart, CheckCircle } from 'lucide-react';
import MorphButton from './MorphButton';
import { requireAuth } from './AuthGate';

const PRESET_AMOUNTS = [10, 50, 100, 200];

export default function DonationModal({ deceasedName, onClose }) {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [donationComplete, setDonationComplete] = useState(false);

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setCustomAmount(value);
      setSelectedAmount(null);
    }
  };

  const handleProceed = () => {
    const amount = customAmount || selectedAmount;
    if (!amount || amount <= 0) {
      toast.error('Please select or enter a donation amount', { position: 'top-center' });
      return;
    }
    setShowQR(true);
  };

  const handleConfirmDonation = () => {
    setDonationComplete(true);
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const finalAmount = customAmount || selectedAmount;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>

        {!showQR && !donationComplete && (
          <>
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Heart className="h-5 w-5 text-accent" />
                Give Niat (Donation)
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Support the family of {deceasedName}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-base mb-3 block">Select Amount</Label>
                <div className="grid grid-cols-2 gap-3">
                  {PRESET_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      variant={selectedAmount === amount ? 'default' : 'outline'}
                      className="h-14 text-lg font-semibold"
                      onClick={() => handleAmountSelect(amount)}
                    >
                      ${amount}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <div>
                <Label htmlFor="custom-amount" className="text-base mb-2 block">
                  Custom Amount
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-muted-foreground">$</span>
                  <Input
                    id="custom-amount"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    className="h-14 pl-8 text-lg"
                  />
                </div>
              </div>

              <Button
                onClick={handleProceed}
                className="w-full h-12 text-base"
                size="lg"
                disabled={!finalAmount}
              >
                Proceed to Payment - ${finalAmount || 0}
              </Button>
            </CardContent>
          </>
        )}

        {showQR && !donationComplete && (
          <>
            <CardHeader>
              <CardTitle className="text-xl">Scan to Pay</CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Amount: ${finalAmount}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-white p-6 rounded-lg border-2">
                {/* Mock PayNow QR Code */}
                <div className="w-full aspect-square bg-gradient-to-br from-primary/20 to-accent/20 rounded flex items-center justify-center">
                  <svg
                    viewBox="0 0 200 200"
                    className="w-full h-full"
                  >
                    <rect width="200" height="200" fill="white" />
                    {/* Mock QR pattern */}
                    <g fill="black">
                      {[...Array(10)].map((_, i) => 
                        [...Array(10)].map((_, j) => 
                          Math.random() > 0.5 ? (
                            <rect
                              key={`${i}-${j}`}
                              x={10 + j * 18}
                              y={10 + i * 18}
                              width="15"
                              height="15"
                            />
                          ) : null
                        )
                      )}
                    </g>
                    <text x="100" y="100" textAnchor="middle" fontSize="24" fill="hsl(var(--primary))" fontWeight="bold">
                      PayNow
                    </text>
                  </svg>
                </div>
                <p className="text-center text-sm text-muted-foreground mt-3">
                  Scan with your banking app
                </p>
              </div>

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  After payment, click the button below
                </p>
                <MorphButton
                  as={Button}
                  data-testid="btn-confirm-donation"
                  idleLabel="I Have Completed Payment"
                  successLabel="Payment Received"
                  onClick={requireAuth}
                  onComplete={handleConfirmDonation}
                  holdMs={700}
                  className="w-full h-12 text-base"
                  size="lg"
                />
                <Button
                  variant="ghost"
                  onClick={() => setShowQR(false)}
                  className="w-full"
                >
                  Change Amount
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {donationComplete && (
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold mb-2">Thank You</h3>
            <p className="text-muted-foreground">
              Your contribution of ${finalAmount} has been received. May Allah reward your generosity.
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
